import { Accordion, Group, Radio, Stack, Text } from '@mantine/core';
import DatasetIcon from '@/components/DatasetIcon.tsx';
import { formatCalendarStyle } from '@/utils/datetime.ts';
import classes from '@/pages/AutomationPage/components/ManualRunModal/components/PreviouslyUploadedFiles/PreviouslyUploadedFiles.module.css';
import { RunGroup } from '@/pages/AutomationPage/components/ManualRunModal/hooks/runGroupMatching.ts';
import { RunWithDatasets } from '@/types/runs.ts';
import RunHistoryPopover from '@/pages/AutomationPage/components/ManualRunModal/components/PreviousRunUI/RunHistoryPopover.tsx';

type Props = {
  group: RunGroup;
  isLast: boolean;
  selectedPreviousRunId?: string;
  getRunUrl: (runId: string) => string;
  onSelectPerfectRun?: (run: RunWithDatasets) => void;
};

const PerfectRunGroupItem = ({
  group,
  isLast,
  selectedPreviousRunId,
  getRunUrl,
  onSelectPerfectRun,
}: Props) => {
  const run = group.latest;
  const additionalRunsCount = group.count - 1;

  return (
    <Accordion.Item
      value={run.run.id}
      key={group.key}
      style={{ border: isLast ? 0 : undefined }}
    >
      <Radio.Card
        checked={run.run.id === selectedPreviousRunId}
        onClick={() => onSelectPerfectRun?.(run)}
        withBorder={false}
      >
        <Group
          wrap="nowrap"
          align="center"
          px={'md'}
          py={'xs'}
          style={{ minWidth: 0 }}
          gap={'xs'}
        >
          <Accordion.Control
            style={{ flex: '0 0 28px', borderRadius: '50%' }}
            px={0}
            ml={'-8px'}
            mr={'-4px'}
            my={'-4px'}
            h={'28px'}
            styles={{
              chevron: {
                width: '100%',
                height: '100%',
                justifyContent: 'center',
                color: '#757575',
              },
            }}
            onClick={(event) => event.stopPropagation()}
          />

          <Text size={'12px'} truncate="end" style={{ flex: 1, minWidth: 0 }}>
            {run.run.name}
          </Text>

          <Group wrap={'nowrap'} ml={'auto'} style={{ flex: '0 0 auto' }} gap={6}>
            <RunHistoryPopover group={group} getRunUrl={getRunUrl} title="Runs with the same inputs">
              <Group gap={4} wrap="nowrap" style={{ cursor: 'pointer' }}>
                <Text size="11px" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {formatCalendarStyle(run.run.created_at)}
                </Text>
                {additionalRunsCount > 0 && (
                  <Text size="11px" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    +{additionalRunsCount}
                  </Text>
                )}
              </Group>
            </RunHistoryPopover>

            <Radio.Indicator className={classes.radioIndicator} ml={4} />
          </Group>
        </Group>
      </Radio.Card>

      <Accordion.Panel styles={{ content: { padding: 0 } }}>
        <Stack px={'xl'} py={'sm'} style={{ background: '#F8F8F8' }} gap={'xs'}>
          {group.missingOptionalTriggers.length > 0 && (
            <Stack gap={4}>
              <Text size="12px" fw={500}>
                All required inputs matched
              </Text>
              <Text size="11px" c="dimmed">
                Optional inputs omitted in that run will stay blank if you reuse it.
              </Text>
            </Stack>
          )}

          {run.input_datasets.map((dataset) => (
            <Group
              wrap={'nowrap'}
              key={dataset.id}
              w="100%"
              style={{ minWidth: 0 }}
              gap={'xs'}
            >
              <DatasetIcon dataset={dataset.dataset} size={'16px'} />
              <Text
                size="12px"
                truncate={'end'}
                style={{ minWidth: 0 }}
                title={dataset.dataset.name}
              >
                {dataset.dataset.name}
              </Text>
            </Group>
          ))}
          {run.input_datasets.length === 0 && (
            <Text
              size="12px"
              truncate={'end'}
              style={{ minWidth: 0, textAlign: 'center' }}
              c={'dimmed'}
            >
              No inputs for this run
            </Text>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default PerfectRunGroupItem;
